import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import YhButton from "../../../../ui/YhButton";
import { getCronSettings, updateCronSettings } from "./SystemSettingsSerive";
import { openSuccessNotification } from "../../../../helpers/methods";
import urls from "../../../../global/constants/UrlConstants";

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseSettings?: () => void;
}

const SystemSettings: React.FC<CustomProps> = (props = {}) => {
  const history = useHistory();
  const isEmbedded = props.isDialogOpen !== undefined;
  const [isEnabled, setIsEnabled] = useState<any>({
    housingCronEnabled: 0,
    acresCronEnabled: 0,
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const response = await getCronSettings();
    setIsEnabled({
      housingCronEnabled: response.data.housingCronEnabled === true ? 1 : 0,
      acresCronEnabled: response.data.acresCronEnabled === true ? 1 : 0,
    });
  };

  const handleClose = () => {
    if (props.handleCloseSettings) {
      props.handleCloseSettings();
      return;
    }
    history.push(urls.accountSettingsViewPth);
  };

  const handleCronJonSetting = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const status = event.target.checked;
    setIsEnabled({ ...isEnabled, housingCronEnabled: status });
    const response = await updateCronSettings({ housingCronEnabled: status });
    if (response.success === 1) {
      openSuccessNotification(response.message);
    }
  };

  const handleAcresCronJonSetting = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const status = event.target.checked;
    setIsEnabled({ ...isEnabled, acresCronEnabled: status });
    const response = await updateCronSettings({ acresCronEnabled: status });
    if (response.success === 1) {
      openSuccessNotification(response.message);
    }
  };

  const Toggle = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={!!checked}
        onChange={onChange}
      />
      <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
      <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
    </label>
  );

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,560px)] w-full max-w-2xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B]">
            System Settings
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Configure system preferences and integrations.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden pb-20">
        <section className="flex h-full min-h-0 flex-col gap-4 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EEF0F5] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[#111827]">
                Housing Cron Job Scheduler
              </p>
              <p className="mt-0.5 text-xs text-[#6B7280]">
                Enable or disable housing cron jobs.
              </p>
            </div>
            <Toggle
              checked={!!isEnabled.housingCronEnabled}
              onChange={handleCronJonSetting}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EEF0F5] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[#111827]">
                99 Acres Cron Job Scheduler
              </p>
              <p className="mt-0.5 text-xs text-[#6B7280]">
                Enable or disable 99 Acres cron jobs.
              </p>
            </div>
            <Toggle
              checked={!!isEnabled.acresCronEnabled}
              onChange={handleAcresCronJonSetting}
            />
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <YhButton variant="secondary" size="sm" onClick={handleClose}>
          Back
        </YhButton>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-2xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default SystemSettings;
